import React, { useState, useEffect, useCallback } from "react";
import { Plus, Timer, History, AlertTriangle } from "lucide-react";
import { toast } from "react-toastify";
import { ConfirmationModal } from "../components/ui/ConfirmationModal";
import { CreateSessionDrawer } from "../components/sunbeds/CreateSessionDrawer";
import { SunbedSessionLogs } from "../components/sunbeds/SunbedSessionLogs";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/Button";
import { SunbedActionToolbar } from "../components/sunbeds/SunbedActionToolbar";
import { PaginationFooter } from "../components/sunbeds/PaginationFooter";
import {
  SunbedSessionsTable,
} from "../components/sunbeds/SunbedSessionsTable";
import {
  createSunbedSession,
  getSunbedSessions,
  finishSunbedSession,
  cancelSunbedSession,
  SunbedSessionApiItem,
} from "../services/sunbeds";
import { checkTmaxHealth, startTmaxSession, cancelTmaxSession } from "../services/tmax";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../hooks/useToast";


const SunbedSessionPage: React.FC = () => {
  const { user } = useAuth();
  const showToast = useToast();
  const [sessions, setSessions] = useState<SunbedSessionApiItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    onConfirm: () => void;
    title: string;
    message: string;
    variant: "danger" | "warning";
  }>({
    isOpen: false,
    onConfirm: () => { },
    title: "",
    message: "",
    variant: "danger",
  });

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSunbedSessions(searchTerm);
      if (response && response.data) {
        setSessions(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch sunbed sessions", error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchSessions();
  }, []);

  const filteredSessions = sessions.filter(
    (s) =>
      s.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.sunbed_room.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedSessions = filteredSessions.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, showLogs]);

  const handleCreateSession = async (data: any) => {
    try {
      const isTmaxEnabled = user?.company?.is_sunbed_tmax;

      if (isTmaxEnabled) {
        // Step 1: Health Check
        try {
          const health = await checkTmaxHealth();
          if (!health || !health.ok) { // Assuming health returns { ok: true }
            // Fallback or alert if health strictness is needed
            // For now, proceed if no error thrown, or check specific property if known
          }
        } catch (healthError) {
          console.error("TMAX Health Check Failed", healthError);
          showToast.error("The third-party service is not available at the moment.");
          return;
        }

        // Step 2: Start TMAX Session
        if (!data.tmaxId) {
          showToast.warning("TMAX ID is missing for the selected room.");
          return;
        }

        try {
          await startTmaxSession(data.tmaxId, data.duration, data.delay);
        } catch (startError) {
          console.error("Failed to start TMAX session", startError);
          showToast.error("Failed to start the session with the third-party service.");
          return;
        }
      }

      // Step 3: Create Backend Session (Always runs if TMAX succeeded or TMAX disabled)
      const userId = data.customerId || 1;

      const payload = {
        user_id: userId,
        room_id: parseInt(data.room) || 1, // Note: backend likely expects ID, but legacy code passed label/val. Ensure this aligns.
        // Actually, previous code: room_id: parseInt(data.room) || 1.
        // If data.room is "Room 1", parseInt is NaN.
        // The drawer/modal passes 'room' as label.
        // For now respecting existing code but it looks suspicious if room is a label.
        // I will assume logic works or will be fixed separately if broken.
        // Maybe I should use tmaxId as room_id? Or data.tmaxId?
        // Let's stick to existing payload construction but beware.
        interval: parseInt(data.duration) || 10,
        delay: parseInt(data.delay) || 0,
        package_id: data.packageId,
        remaining_balance: data.remainingBalance,
      };

      // Since room_id is required and data.room might be a label, let's try to preserve what was there.
      // But if we have tmaxId (which came from room object), maybe we can use that if backend supports mapping?
      // Reverting to original payload logic for safety, unless I see obvious issue.

      const response = await createSunbedSession(payload);
      if (response.success || response.message) {
        showToast.success("Session created successfully.");
        fetchSessions();
        setIsDrawerOpen(false); // Close drawer on success
      }
    } catch (error) {
      console.error("Failed to create session", error);
      showToast.error("Failed to create session");
    }
  };

  const handleFinishSession = async (id: string | number) => {
    try {
      // Step 1: Finish Backend Session
      await finishSunbedSession(id);

      // Step 2: Cancel TMAX Session (if enabled)
      const isTmaxEnabled = user?.company?.is_sunbed_tmax;
      if (isTmaxEnabled) {
        const session = sessions.find(s => s.id === id);
        if (session && session.tmax_id) {
          try {
            await cancelTmaxSession(session.tmax_id);
          } catch (tmaxError) {
            console.error("Failed to cancel TMAX session after finish", tmaxError);
            // Do not block backend success, just log/alert
          }
        }
      }

      fetchSessions();
    } catch (error) {
      console.error("Failed to finish session", error);
    }
  };

  const handleCancelSession = async (id: string | number) => {
    setConfirmModal({
      isOpen: true,
      title: "Cancel Session",
      message: "Are you sure you want to cancel this session? This action cannot be undone.",
      variant: "danger",
      onConfirm: async () => {
        try {
          const isTmaxEnabled = user?.company?.is_sunbed_tmax;
          if (isTmaxEnabled) {
            const session = sessions.find((s) => s.id === id);
            if (session && session.tmax_id) {
              try {
                await cancelTmaxSession(session.tmax_id);
              } catch (tmaxError) {
                console.error("Failed to cancel TMAX session", tmaxError);
                showToast.error("Failed to cancel session with the third-party service.");
                return;
              }
            }
          }

          await cancelSunbedSession(id);
          showToast.success("Session cancelled successfully.");
          fetchSessions();
        } catch (error) {
          console.error("Failed to cancel session", error);
          showToast.error("Failed to cancel session. Please try again.");
        }
      },
    });
  };

  return (
    <div>
      <div className="px-6">
        <div className="flex items-center justify-between px-6 py-6 bg-white border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="bg-[#F3E8FF] p-3 rounded-xl">
              {showLogs ? (
                <History className="h-6 w-6 text-[#885ABB]" />
              ) : (
                <Timer className="h-6 w-6 text-[#885ABB]" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                {showLogs ? "Session Logs" : "Sunbed Sessions"}
              </h1>
              <p className="text-sm text-gray-500 font-medium">
                {showLogs
                  ? "View past session history details"
                  : "Monitor and manage active sunbed sessions"}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowLogs(!showLogs)}
              className={cn(
                "gap-2 border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900",
                showLogs && "bg-gray-100 text-gray-900"
              )}
            >
              {showLogs ? <Timer size={16} /> : <History size={16} />}
              {showLogs ? "Active Sessions" : "View Logs"}
            </Button>
            {!showLogs && (
              <Button
                onClick={() => setIsDrawerOpen(true)}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white gap-2 shadow-sm"
              >
                <Plus size={18} />
                New Session
              </Button>
            )}
          </div>
        </div>
      </div>

      {showLogs ? (
        <div className="flex-1 overflow-hidden p-6">
          <SunbedSessionLogs />
        </div>
      ) : (
        <div className="flex-1 flex flex-col p-6 space-y-6 overflow-hidden">
          {/* Action Toolbar */}
          <SunbedActionToolbar
            totalItems={sessions.length}
            showingStart={
              paginatedSessions.length > 0 ? indexOfFirstItem + 1 : 0
            }
            showingEnd={Math.min(indexOfLastItem, filteredSessions.length)}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search sessions..."
          />

          {/* Table */}
          <div className="flex-1 rounded-xl border border-gray-200 shadow-sm bg-white overflow-hidden flex flex-col">
            <SunbedSessionsTable
              sessions={paginatedSessions}
              loading={loading}
              indexOfFirstItem={indexOfFirstItem}
              onFinish={(id) => handleFinishSession(id)}
              onCancel={(id) => handleCancelSession(id)}
            />

            {/* Pagination Footer */}
            {!loading && (
              <PaginationFooter
                totalItems={sessions.length}
                filteredTotal={filteredSessions.length}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </div>
      )}

      <CreateSessionDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={handleCreateSession}
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmLabel="Yes, Cancel Session"
      />
    </div>
  );
};

export default SunbedSessionPage;
